import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Ticket,
  DollarSign,
  UserCheck,
  UserPlus,
  Share2,
  Copy,
  Check
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

// Componente de botones para compartir
function ShareButtons({ title, url }) {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = url || window.location.href;
  const shareText = encodeURIComponent(title || document.title);
  const shareUrlEncoded = encodeURIComponent(shareUrl);
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-500 flex items-center gap-1">
        <Share2 className="w-4 h-4" /> Compartir:
      </span>
      
      {/* WhatsApp */}
      <a
        href={`https://wa.me/?text=${shareText}%20${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors"
        title="Compartir en WhatsApp"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
      
      {/* Facebook */}
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        title="Compartir en Facebook"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </a>
      
      {/* Twitter/X */}
      <a
        href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-black hover:bg-gray-800 text-white transition-colors"
        title="Compartir en X (Twitter)"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </a>
      
      {/* LinkedIn */}
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white transition-colors"
        title="Compartir en LinkedIn"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </a>
      
      {/* Copiar enlace */}
      <button
        onClick={handleCopyLink}
        className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        title={copied ? '¡Copiado!' : 'Copiar enlace'}
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Función para formatear fechas
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export default function EventoDetallePage() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, trabajador } = useAuth();
  
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para modal de inscripción (¿Sos matriculado?)
  const [showMatriculadoModal, setShowMatriculadoModal] = useState(false);

  useEffect(() => {
    fetchEvento();
  }, [eventoId]);

  const fetchEvento = async () => {
    try {
      const res = await axios.get(`${API_URL}/eventos/publicos/${eventoId}`);
      // Redirect al slug canónico si vinieron por un link viejo
      if (res.data?.slug_canonico && res.data.slug_canonico !== eventoId) {
        navigate(`/eventos/${res.data.slug_canonico}`, { replace: true });
        return;
      }
      setEvento(res.data);
    } catch (err) {
      setError('Evento no encontrado');
    } finally {
      setLoading(false);
    }
  };

  // Manejar click en "Inscribirme"
  const handleInscribirseClick = () => {
    // Si ya está autenticado, ir directo a inscripción (con datos precompletados)
    if (isAuthenticated) {
      navigate(`/eventos/${eventoId}/inscribirse`);
    } else {
      // Mostrar modal preguntando si es matriculado
      setShowMatriculadoModal(true);
    }
  };

  // Manejar respuesta del modal de matriculado
  const handleSoyMatriculado = () => {
    setShowMatriculadoModal(false);
    // Guardar la URL de retorno para después del login
    sessionStorage.setItem('redirectAfterLogin', `/eventos/${eventoId}/inscribirse`);
    navigate('/login');
  };

  const handleNoSoyMatriculado = () => {
    setShowMatriculadoModal(false);
    // Ir directo al formulario de inscripción (para otros profesionales)
    navigate(`/eventos/${eventoId}/inscribirse`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!evento) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Calendar className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">Evento no encontrado</p>
        <button onClick={() => goBack()} className="text-violet-600 hover:underline">
          ← Volver
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button onClick={() => goBack()} className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Info del evento */}
        <Card className="mb-6 overflow-hidden">
          {evento.imagen_portada && (
            <div className="bg-gray-100 flex justify-center p-4">
              <img 
                src={evento.imagen_portada} 
                alt={evento.nombre}
                className="max-h-72 object-contain rounded-lg"
              />
            </div>
          )}
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {evento.modalidad_admision === 'ticket_qr' && (
                <Badge className="bg-violet-100 text-violet-700">
                  <Ticket className="w-3 h-3 mr-1" />
                  Con entrada
                </Badge>
              )}
              {evento.configuracion_pago?.tipo === 'gratis' && (
                <Badge className="bg-green-100 text-green-700">Gratis</Badge>
              )}
              {evento.configuracion_pago?.tipo === 'pago_fijo' && (
                <Badge className="bg-amber-100 text-amber-700">
                  <DollarSign className="w-3 h-3 mr-1" />
                  ${evento.configuracion_pago.precio?.toLocaleString()}
                </Badge>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">{evento.nombre}</h1>
            
            {/* Botones de compartir */}
            <div className="mb-6 pb-4 border-b">
              <ShareButtons 
                title={`${evento.nombre} - Colegio de Trabajadores Sociales`} 
                url={window.location.href}
              />
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6 text-sm">
              {evento.fecha_evento && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-5 h-5 text-violet-600" />
                  <span>{formatDate(evento.fecha_evento)}</span>
                </div>
              )}
              {evento.hora_evento && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-5 h-5 text-violet-600" />
                  <span>{evento.hora_evento} hs</span>
                </div>
              )}
              {evento.lugar && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-5 h-5 text-violet-600" />
                  <span>{evento.lugar}</span>
                </div>
              )}
            </div>

            {evento.cupo_maximo && (
              <div className="flex items-center gap-2 text-sm mb-4">
                <Users className="w-5 h-5 text-gray-400" />
                <span className={evento.cupo_agotado ? 'text-red-600' : 'text-gray-600'}>
                  {evento.cupo_agotado 
                    ? 'Cupo agotado' 
                    : `${evento.cupo_disponible} lugares disponibles`}
                </span>
              </div>
            )}

            {evento.descripcion && (
              <div className="prose prose-sm max-w-none text-gray-600">
                <p className="whitespace-pre-wrap">{evento.descripcion}</p>
              </div>
            )}

            {/* Media promocional (flyer/video) */}
            {evento.media_promocional && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-semibold text-gray-500 mb-3">📸 Material promocional</h3>
                <div className="flex justify-center">
                  {evento.media_promocional_tipo === 'video' ? (
                    evento.media_promocional.includes('youtube') || evento.media_promocional.includes('youtu.be') ? (
                      <iframe
                        width="100%"
                        height="315"
                        style={{ maxWidth: '560px' }}
                        src={evento.media_promocional.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                        title="Video del evento"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="rounded-lg"
                      />
                    ) : (
                      <video 
                        src={evento.media_promocional} 
                        controls 
                        className="max-w-full rounded-lg"
                        style={{ maxHeight: '400px' }}
                      />
                    )
                  ) : (
                    <img 
                      src={evento.media_promocional} 
                      alt="Flyer del evento" 
                      className="max-w-full rounded-lg shadow-md"
                      style={{ maxHeight: '500px' }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Botón de inscripción */}
            <div className="mt-8 pt-6 border-t">
              {evento.cupo_agotado ? (
                evento.habilitar_lista_espera ? (
                  <div className="text-center space-y-4">
                    <div>
                      <Clock className="w-12 h-12 text-orange-400 mx-auto mb-2" />
                      <p className="text-gray-700 font-medium">Cupo agotado para este evento</p>
                      <p className="text-gray-500 text-sm mt-1">
                        Pero podés anotarte en la lista de espera
                      </p>
                    </div>
                    <Button 
                      onClick={handleInscribirseClick}
                      className="w-full md:w-auto bg-orange-500 hover:bg-orange-600"
                      data-testid="btn-lista-espera"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Anotarme en lista de espera
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-2" />
                    <p className="text-gray-500">Cupo agotado para este evento</p>
                  </div>
                )
              ) : !evento.inscripciones_abiertas ? (
                evento.habilitar_lista_espera ? (
                  <div className="text-center space-y-4">
                    <div>
                      <Clock className="w-12 h-12 text-orange-400 mx-auto mb-2" />
                      <p className="text-gray-700 font-medium">Inscripciones cerradas</p>
                      <p className="text-gray-500 text-sm mt-1">
                        Pero podés anotarte en la lista de espera
                      </p>
                    </div>
                    <Button 
                      onClick={handleInscribirseClick}
                      className="w-full md:w-auto bg-orange-500 hover:bg-orange-600"
                      data-testid="btn-lista-espera"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Anotarme en lista de espera
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-2" />
                    <p className="text-gray-500">Inscripciones cerradas para este evento</p>
                  </div>
                )
              ) : (
                <Button 
                  onClick={handleInscribirseClick}
                  className="w-full md:w-auto bg-violet-600 hover:bg-violet-700"
                  data-testid="btn-inscribirse"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Inscribirme
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Modal de Matriculado - ¿Sos matriculade de Lomas de Zamora? */}
      <Dialog open={showMatriculadoModal} onOpenChange={setShowMatriculadoModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-violet-600" />
              ¿Sos matriculade de Lomas de Zamora?
            </DialogTitle>
            <DialogDescription className="pt-2">
              En casos de cupos limitados, priorizamos la inscripción de quienes son matriculades de nuestro distrito. 
              Si sos matriculade, iniciá sesión para completar tu inscripción.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={handleNoSoyMatriculado}
              className="w-full sm:w-auto"
              data-testid="btn-no-soy-matriculade"
            >
              No, no soy matriculade
            </Button>
            <Button 
              onClick={handleSoyMatriculado}
              className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700"
              data-testid="btn-soy-matriculade"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Sí, soy matriculade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Colegio Profesional de Trabajadorxs Sociales - Distrito Lomas de Zamora</p>
        </div>
      </footer>
    </div>
  );
}
